import { auth } from './firebase';

const GCF_BASE_URL = '/api/mealpai-ai';

async function getIdToken(): Promise<string> {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    return user.getIdToken();
}

async function callGuidedApi<T>(
    path: string,
    options: { method?: string; body?: unknown; auth?: boolean } = {},
): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (options.auth !== false) {
        headers.Authorization = `Bearer ${await getIdToken()}`;
    }

    const response = await fetch(`${GCF_BASE_URL}${path}`, {
        method: options.method || 'POST',
        headers,
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });

    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(typeof json.error === 'string' ? json.error : 'Request failed');
    }
    return json as T;
}

export async function peekGuidedAccessToken(token: string): Promise<{ valid: true }> {
    return callGuidedApi('/guided-access/peek', { body: { token }, auth: false, method: 'POST' });
}

export async function exchangeGuidedAccessToken(token: string, email: string): Promise<{
    customToken: string;
    clientId: string;
    consultantId: string;
    householdId: string;
}> {
    return callGuidedApi('/guided-access/exchange', { body: { token, email }, auth: false, method: 'POST' });
}

export async function createGuidedClient(input: {
    email: string;
    firstName: string;
    lastName?: string;
    consultantId: string;
}): Promise<{
    clientId: string;
    accessId: string;
    householdId: string;
    accessUrl: string;
}> {
    return callGuidedApi('/guided-clients', { body: input, method: 'POST' });
}

export async function refreshGuidedClientAccess(accessId: string): Promise<{ accessUrl: string }> {
    return callGuidedApi(`/guided-client-access/${accessId}/refresh`, { method: 'POST' });
}

export async function setGuidedClientAccessActive(accessId: string, isActive: boolean): Promise<void> {
    await callGuidedApi(`/guided-client-access/${accessId}/active`, {
        method: 'PATCH',
        body: { isActive },
    });
}

export async function updateGuidedClientProfile(
    clientId: string,
    updates: Record<string, unknown>,
): Promise<void> {
    await callGuidedApi(`/guided-clients/${clientId}`, { method: 'PATCH', body: updates });
}

export async function updateGuidedClientSelf(
    clientId: string,
    updates: { knownAllergies?: unknown; userDietTypes?: unknown; dietPreference?: string },
): Promise<void> {
    await callGuidedApi(`/guided-clients/${clientId}/self`, { method: 'PATCH', body: updates });
}

export async function changeGuidedClientEmail(
    clientId: string,
    accessId: string,
    email: string,
): Promise<{ accessUrl: string }> {
    return callGuidedApi(`/guided-clients/${clientId}/email`, {
        method: 'PATCH',
        body: { email, accessId },
    });
}

export async function upgradeGuidedClientToPro(clientId: string): Promise<{ personalHouseholdId: string }> {
    return callGuidedApi(`/guided-clients/${clientId}/go-pro`, { method: 'POST' });
}

export async function createTempUserForHousehold(input: {
    email: string;
    firstName: string;
    lastName?: string;
    householdId: string;
}): Promise<{
    clientId: string;
    accessId: string;
    householdId: string;
    accessUrl: string;
}> {
    return callGuidedApi(`/households/${input.householdId}/temp-clients`, { body: input, method: 'POST' });
}

export async function deleteTempUserAccess(householdId: string, accessId: string): Promise<void> {
    await callGuidedApi(`/households/${householdId}/temp-clients/${accessId}`, { method: 'DELETE' });
}
